// src/services/purchaseOrdersService.ts
// Fetches live purchase orders from the Cloudflare Worker API (Neon database).
// Maps the API response to the PO shape PurchaseOrders.tsx expects.
//
// Field notes:
//   purchaseOrders.totalValue  → totalExGST  (what the UI calls it)
//   purchaseOrders.gstAmount   → gst
//   totalValue + gstAmount     → totalIncGST (calculated)
//   supplier.name              → supplier AND supplierName (UI renders po.supplier)
//   lineItems / lines          → [] for list; populated from GET /:id detail

import { api } from '../utils/api'

// What the API returns (list endpoint with supplier join)
interface ApiPO {
  id:           string
  poNumber?:    string
  po_number?:   string
  status?:      string
  description?: string | null
  totalValue?:  string | number | null   // Drizzle decimal → string
  total_value?: string | number | null
  gstAmount?:   string | number | null
  gst_amount?:  string | number | null
  deliveryDate?: string | null
  delivery_date?: string | null
  notes?:       string | null
  jobId?:       string | null
  job_id?:      string | null
  supplierId?:  string | null
  supplier_id?: string | null
  approvedAt?:  string | null
  approved_at?: string | null
  createdAt?:   string
  created_at?:  string
  // Joined from suppliers table
  supplier?: { name?: string; email?: string; phone?: string } | null
}

// What PurchaseOrders.tsx expects — matches typeof MOCK_POS[0]
export interface MappedPO {
  id:              string
  poNumber:        string
  jobId:           string
  jobNumber:       string        // not in API list — defaulted; enriched in a future step
  supplierId:      string
  supplier:        string        // what the table renders: po.supplier
  supplierName:    string        // alias — kept for mapPOForDocument compat
  supplierEmail:   string
  type:            string        // not in schema yet — defaulted to 'MATERIALS'
  status:          string
  description:     string
  totalExGST:      number        // from totalValue
  gst:             number        // from gstAmount
  totalIncGST:     number        // totalValue + gstAmount
  deliveryDate:    string | null
  notes:           string | null
  approvedAt:      string | null
  approvedById:    string | null
  approvedByName:  string        // not in list response — defaulted
  createdAt:       string
  createdByName:   string        // not in list response — defaulted
  lines:           never[]       // empty in list; populated from GET /:id
  lineItems:       never[]       // alias — for mapPOForDocument compat
}

function num(v: string | number | null | undefined): number {
  if (v == null) return 0
  return typeof v === 'number' ? v : parseFloat(v) || 0
}

function mapApiPOToAppPO(raw: ApiPO): MappedPO {
  const supplierName = raw.supplier?.name ?? ''
  const totalExGST   = num(raw.totalValue   ?? raw.total_value)
  const gst          = num(raw.gstAmount    ?? raw.gst_amount)

  return {
    id:             raw.id,
    poNumber:       raw.poNumber    ?? raw.po_number    ?? '',
    jobId:          raw.jobId       ?? raw.job_id       ?? '',
    jobNumber:      '',              // enriched from jobs join in a future step
    supplierId:     raw.supplierId  ?? raw.supplier_id  ?? '',
    // Both names — UI renders po.supplier; mapPOForDocument uses po.supplier??po.supplierName
    supplier:       supplierName,
    supplierName,
    supplierEmail:  raw.supplier?.email ?? '',
    type:           'MATERIALS',     // type column planned for future migration
    status:         raw.status      ?? 'DRAFT',
    description:    raw.description ?? '',
    totalExGST,
    gst,
    totalIncGST:    totalExGST + gst,
    deliveryDate:   raw.deliveryDate ?? raw.delivery_date ?? null,
    notes:          raw.notes       ?? null,
    approvedAt:     raw.approvedAt  ?? raw.approved_at  ?? null,
    approvedById:   null,
    approvedByName: '',              // enriched from users join in a future step
    createdAt:      raw.createdAt   ?? raw.created_at   ?? new Date().toISOString(),
    createdByName:  '',
    lines:          [],
    lineItems:      [],
  }
}

export async function fetchPurchaseOrders(jobId?: string): Promise<MappedPO[]> {
  const params = jobId ? `?jobId=${jobId}` : ''
  const { data } = await api.get<{ data: ApiPO[] } | ApiPO[]>(`/api/purchase-orders${params}`)
  const raw: ApiPO[] = Array.isArray(data) ? data : ((data as any).data ?? [])
  return raw.map(mapApiPOToAppPO)
}
